import uniq from 'lodash/uniq';

import type { FxRateContract } from '@luxuryescapes/contract-fx';

import { DEFAULT_CURRENCY } from '~/components/Offers/Detail/OfferBuilder/DealInformation/tabs/dealOptions/dealOptionsFormatters';

import type { OpportunityRoomAsset } from '~/hooks/apis/offers/useGetOffer';
import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { deriveOpportunityCurrency } from '../inclusions/inclusionsModel';

import { LOCATION_OPTIONS, type LocationTree } from './locationOptions';
import type { PropertySetupValues } from './types';

interface PropertySetupOpportunity {
  opportunity_info?: { cost_price_currency__c?: string | null } | null;
  location?: {
    ac_deal_category__c?: string | null;
    ac_deal_sub_category__c?: string | null;
    destination__c?: string | null;
    sub_destination__c?: string | null;
  } | null;
  key_dates?: {
    expiry_date_travel_from__c?: string | null;
    travel_to__c?: string | null;
  } | null;
}

interface DerivePropertySetupInput {
  roomAssets?: Array<OpportunityRoomAsset>;
  accountName?: string | null;
  opportunity?: PropertySetupOpportunity | null;
  deals?: Array<OpportunityRelatedDeal>;
}

const ISO_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})/;

// Salesforce sends dates or timestamps; ReadOnlyField type 'date' needs YYYY-MM-DD.
export function toIsoDate(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  return ISO_DATE_PREFIX.exec(value)?.[1] ?? null;
}

export function deriveOpportunityPrefill(opportunity?: PropertySetupOpportunity | null) {
  const location = opportunity?.location;

  return {
    dealCategory: location?.ac_deal_category__c || null,
    offerCountry: location?.ac_deal_sub_category__c || null,
    destination: location?.destination__c || null,
    subDestination: location?.sub_destination__c || null,
    travelFrom: toIsoDate(opportunity?.key_dates?.expiry_date_travel_from__c),
    travelTo: toIsoDate(opportunity?.key_dates?.travel_to__c),
  };
}

export type TravelWindow = { travelFrom: string | null; travelTo: string | null };

// Dates copied from a previous Opportunity can't apply once they miss this one's travel dates or are over;
// Opportunities early in the pipeline often have no travel dates yet, so only the second check runs for them.
export function staleRangeReason(
  { startDate, endDate }: { startDate: string; endDate: string },
  { travelFrom, travelTo }: TravelWindow,
  today: string,
): 'outsideTravel' | 'ended' | undefined {
  if (travelFrom && travelTo && (endDate < travelFrom || startDate > travelTo)) {
    return 'outsideTravel';
  }
  return endDate < today ? 'ended' : undefined;
}

export function derivePropertySetup({
  roomAssets = [],
  accountName,
  opportunity,
  deals = [],
}: DerivePropertySetupInput): PropertySetupValues {
  const assetAccountName = roomAssets.find((asset) => asset?.account__name)?.account__name;

  return {
    propertyName: assetAccountName || accountName || null,
    ...deriveOpportunityPrefill(opportunity),
    currency: opportunity?.opportunity_info?.cost_price_currency__c || deriveOpportunityCurrency(deals),
    fxRate: null,
    prvReqLeadPrice: null,
  };
}

// Parent first: applyLocationChange clears every field after the changed one.
export const LOCATION_FIELDS = ['dealCategory', 'offerCountry', 'destination', 'subDestination'] as const;

export type LocationField = (typeof LOCATION_FIELDS)[number];

type LocationValues = Pick<PropertySetupValues, LocationField>;

interface LocationFieldOptions {
  options: Array<string>;
  disabled: boolean;
}

// Saved values stay listed so prefilled or draft values outside the tree still show.
export function deriveLocationOptions(
  values: LocationValues,
  tree: LocationTree = LOCATION_OPTIONS,
): Record<LocationField, LocationFieldOptions> {
  let node: LocationTree | undefined = tree;

  return Object.fromEntries(
    LOCATION_FIELDS.map((field) => {
      const saved = values[field];
      const children = Array.isArray(node) ? node : Object.keys(node ?? {});
      node = saved && node && !Array.isArray(node) ? node[saved] : undefined;
      // A saved value outside the tree keeps its dropdown enabled so it can be cleared with None.
      const options = uniq(saved ? [...children, saved] : children);
      return [field, { options, disabled: options.length === 0 }];
    }),
  ) as Record<LocationField, LocationFieldOptions>;
}

// Countries sit under each deal category; without one, a country under any category counts.
export function findLocationCountry(
  name: string,
  dealCategory: string | null,
  tree: LocationTree = LOCATION_OPTIONS,
): string | null {
  if (Array.isArray(tree)) {
    return null;
  }
  const wanted = name.trim().toLowerCase();
  const categories = dealCategory ? [tree[dealCategory]] : Object.values(tree);
  for (const countries of categories) {
    const match =
      countries && !Array.isArray(countries) && Object.keys(countries).find((c) => c.toLowerCase() === wanted);
    if (match) {
      return match;
    }
  }
  return null;
}

export function applyLocationChange<T extends LocationValues>(
  values: T,
  field: LocationField,
  value: string | null,
): T {
  const children = LOCATION_FIELDS.slice(LOCATION_FIELDS.indexOf(field) + 1);
  return { ...values, [field]: value, ...Object.fromEntries(children.map((child) => [child, null])) };
}

export function netRateFor(values: Pick<PropertySetupValues, 'currency' | 'fxRate'> | null | undefined) {
  const currency = values?.currency ?? DEFAULT_CURRENCY;
  return currency === DEFAULT_CURRENCY ? 1 : (values?.fxRate ?? undefined);
}

export function deriveCurrencyOptions(rates: Array<FxRateContract.FxRate> | undefined): Array<string> {
  return uniq([DEFAULT_CURRENCY, ...(rates ?? []).map((rate) => rate.currency)]).sort();
}

export function resolveFxRate(
  rates: Array<FxRateContract.FxRate> | undefined,
  currency: string | null | undefined,
): number | null {
  if (!currency) {
    return null;
  }

  if (currency === 'AUD') {
    return 1;
  }

  return rates?.find((rate) => rate.currency === currency)?.rate ?? null;
}
